import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import Modal from '@storybook/new-modal/modal';
import { API_URL } from 'constant';
import {
	ComplexSessionNodesState,
	ConditionalSessionFlowState,
	FundDetailState,
	SelectedSessionState,
	SelectedSessionsIdState,
	SessionItemSelected,
	isFundCallingState,
	FundInvestorFormErrorHandlerState,
	FundInvestorModalState,
	FundInvestorState,
	FundsState,
	useFunds,
} from 'global-stores';
import { useNetwork, useNotification } from 'hooks';
import { ROUTES } from 'routes';
import { envHost } from 'helpers';
import { getDate, useFormatNumber } from 'utils';
import { ComplexStepsView } from 'views/pipelines/components/complex-step-view';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import {
	ViewKyc,
	useComplexSessionInfo,
	useSessionDetails,
} from 'views/sessions';
import { ComplexSessionView } from 'views/sessions/components/complex-session-view';
import { FundIconHeader, IFundHeaderApi } from './components/fund-header';
import {
	Kebab,
	PageHead,
	SearchBox,
	StepChain,
	useSv2Theme,
} from 'views/design-system';
import {
	pendingInvestors,
	pendingRaised,
	provenance,
	toInvestorRows,
	toSteps,
	totalRaised,
} from './deck/derive';
import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './deck/fund-detail-deck.scss';
import './funds-Details.scss';

export const FundDetails = () => {
	const [fundDetailList, setfundDetailList] = useRecoilState(FundDetailState);
	const [isFundCalling, setIsFundCalling] = useRecoilState(isFundCallingState);
	const setSelectedIdState = useSetRecoilState(SelectedSessionsIdState);
	const setSessionItemSelected = useSetRecoilState(SessionItemSelected);
	const resetSelectedSessionState = useResetRecoilState(SelectedSessionState);
	const { errorNotification, infoNotification } = useNotification();
	const navigate = useNavigate();
	const { FUNDS } = ROUTES;
	const {
		get: getFundDetail,
		data: FundDetailData,
		error: FundDetailError,
		status: FundDetailStatus,
		isLoaded: FundDetailLoaded,
		loading: FundDetailLoading,
	} = useNetwork();
	const { get: getNodesDetail } = useNetwork({ updateState: false });

	const { fetchActivity, isLoaded: activityLoaded } = useSessionDetails();
	const { fetchDeviceInfo, isLoaded: sessionDeviceLoaded } =
		useSessionDetails();
	const { fetchKyc, isLoaded: sessionKycLoaded } = useSessionDetails();
	const { fetchAml, isLoaded: sessionAmlLoaded } = useSessionDetails();
	const { fetchKyb, isLoaded: sessionKybLoaded } = useSessionDetails();
	const { fetchSignAgreement, isLoaded: sessionSignLoaded } =
		useSessionDetails();
	const { fetchFundInvestment, isLoaded: sessionFundLoaded } =
		useSessionDetails();
	const { fetchAccreditation, isLoaded: sessionAccreditationLoaded } =
		useSessionDetails();
	const { fetchForm } = useSessionDetails();

	const { numberDecimal } = useFormatNumber();

	// Local state
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const seComplexSessionNodes = useSetRecoilState(ComplexSessionNodesState);
	const setIsComplexTab = useSetRecoilState(ConditionalSessionFlowState);
	const [isComplexOpen, setIsComplexOpen] = useState(false);
	const [openConditionalView, setConditionalOpenView] =
		useState<boolean>(false);
	const [selectedItem, setSelectedItem] = useState<any>();
	const [isNodesSession, setIsNodesSession] = useState<boolean>(false);

	const { complexSessionInfo, complexSessionDetailsLoading } =
		useComplexSessionInfo();

	const { checkUserWritePermission } = useUserRoles();

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Funds),
		[checkUserWritePermission]
	);

	const fundId = useMemo(() => {
		const { href: url } = window.location;
		return url.substring(url.lastIndexOf('/') + 1);
	}, []);

	useEffect(() => {
		setfundDetailList([]);
		if (!fundId) return;
		getFundDetail(`${API_URL.FUND_DETAIL}/${fundId}`);
		// eslint-disable-next-line
	}, [fundId]);

	useEffect(() => {
		if (!fundId) return;
		if (isFundCalling) {
			getFundDetail(`${API_URL.FUND_DETAIL}/${fundId}`);
			setIsFundCalling(false);
		}
		// eslint-disable-next-line
	}, [isFundCalling, fundId]);

	useEffect(() => {
		if (FundDetailData?.data && FundDetailStatus) {
			return setfundDetailList(FundDetailData.data);
		}

		if (FundDetailError) {
			errorNotification('Failed to load fund details.');
		}
		// eslint-disable-next-line
	}, [FundDetailData, FundDetailError, FundDetailLoaded]);

	const handleCloseViewModal = useCallback(() => {
		setIsModalOpen((prev: boolean) => !prev);
		resetSelectedSessionState();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getSessionDetails = (id: string) => {
		fetchActivity(id);
		fetchDeviceInfo(id);
		fetchKyc(id);
		fetchAml(id);
		fetchKyb(id);
		fetchSignAgreement(id);
		fetchFundInvestment(id);
		fetchAccreditation(id);
		fetchForm(id);
	};

	const fetchComplexSessionDetails = useCallback(
		(sessionId: string) => {
			complexSessionInfo(sessionId);
		},
		[complexSessionInfo]
	);

	//Shahbaaz TODO  Remove in future
	const validateComplexSession = async ({
		sessionId,
	}: {
		sessionId: string;
	}) => {
		setIsNodesSession(true);
		infoNotification('Please wait while, we fetch the details...');
		const nodesResp = await getNodesDetail(
			`${API_URL.COMPLEX_SESSION_DETAILS}/${sessionId}`
		);
		setIsNodesSession(false);
		if (nodesResp?.message === 'ok') {
			return nodesResp?.data?.nodes ?? [];
		}

		return null;
	};

	const handleClickViewIcon = useCallback(
		(fund: {
				sessionId: string;
				firstName: string;
				lastName: string;
				session?: any;
			}) =>
			async () => {
				const { sessionId, lastName, firstName } = fund ?? {};
				const sesssionNodes = await validateComplexSession({ sessionId });
				if (sesssionNodes) {
					setIsComplexTab(true);
					seComplexSessionNodes(sesssionNodes);
					setIsComplexOpen(true);
					fetchComplexSessionDetails(sessionId);
					setSessionItemSelected({
						name: `${firstName} ${lastName}`,
						_id: sessionId,
						type: 'complex',
					});
				} else {
					setSessionItemSelected({
						name: `${firstName} ${lastName}`,
						_id: sessionId,
					});
					setIsComplexTab(false);
					getSessionDetails(sessionId);
					handleCloseViewModal();
				}
				setSelectedIdState([sessionId]);
			},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[getSessionDetails, handleCloseViewModal]
	);

	const handleViewStepDesign = useCallback((e: any, item: any) => {
		e.stopPropagation();
		setSelectedItem(item);
		setConditionalOpenView(true);
	}, []);

	const handleCloseModal = () => {
		setConditionalOpenView(false);
		setSelectedItem({});
	};

	/* StatusJsx built the old chevron chips for the ReactResponsiveTable cell.
	   The sv2 table uses StepChain, which is the same language as every other
	   list, so this is gone with the row builder. */

	/* ------------------------------------------------- sv2 page state ----- */

	// Hold the theme claim, so arriving from a dark page does not strobe the
	// shell back to light (CLAUDE.md, "Dark mode is claimed, not global").
	useSv2Theme();

	const [search, setSearch] = useState('');
	const headerApi = useRef<IFundHeaderApi | null>(null);
	const funds = useRecoilValue(FundsState);
	const { getFunds } = useFunds();
	const [fundInvestorModalState, setFundInvestorModalState] = useRecoilState(
		FundInvestorModalState
	);
	const resetInvestorList = useResetRecoilState(FundInvestorState);
	const resetInvestorFormErrors = useResetRecoilState(
		FundInvestorFormErrorHandlerState
	);

	/**
	 * §5.1.2 / §5.2 — the fund RECORD (name, round, post-money) has only ever
	 * been looked up in `FundsState`, the LIST payload. On a cold load of
	 * /funds/:id that list is empty, which is why the title and valuation came up
	 * blank and the page read as broken. Fetch the list once when the fund is not
	 * already in it, so the URL works on its own.
	 */
	const fundRecord = useMemo(
		() => (funds ?? []).find((f: any) => f?.id === fundId),
		[funds, fundId]
	);

	useEffect(() => {
		if (!fundId || fundRecord) return;
		getFunds();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundId]);

	const fundName = fundRecord?.name ?? '';
	const fundRound = fundRecord?.fundingRound ?? '';
	const fundPostMoney = Number(fundRecord?.postMoneyValuation ?? 0);

	const openAddInvestor = useCallback(() => {
		// FundIconHeader owns the modal and its stores; use its own opener so the
		// reset order and the isEdit/id contract stay in one place
		if (headerApi.current) {
			headerApi.current.openAdd();
			return;
		}
		setFundInvestorModalState({
			...fundInvestorModalState,
			isModalOpen: true,
			id: '',
			isEdit: false,
		});
		resetInvestorList();
		resetInvestorFormErrors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundInvestorModalState]);

	/**
	 * Edit cap table and Escrow both had `display:none` layered ON TOP of an env
	 * gate, so working code sat behind a rule nobody could see through (§5.2).
	 * The gates are kept and stated; the hiding is not.
	 */
	/**
	 * Download and Escrow are rendered BY FundIconHeader (JsonToCSV is the
	 * downloader itself, FundEscrow is the modal plus its trigger). Clicking
	 * their real controls is what preserves their behaviour exactly — the kebab
	 * reaches them rather than reimplementing a CSV builder and an escrow store.
	 */
	const clickInHeader = useCallback((selector: string) => {
		const el = document.querySelector<HTMLElement>(
			`.fund-detail-summary--chromeless ${selector}`
		);
		if (el) el.click();
	}, []);

	const kebabItems = useMemo(() => {
		const items: { label: string; onClick: () => void }[] = [];
		if (fundDetailList.length) {
			items.push({
				label:
					envHost === 'stage'
						? 'Edit cap table'
						: 'Edit cap table (stage only)',
				onClick: () => {
					if (envHost !== 'stage') {
						infoNotification('Editing a cap table is enabled on stage only.');
						return;
					}
					headerApi.current?.openEdit();
				},
			});
			items.push({
				label: 'Download cap table (CSV)',
				onClick: () => clickInHeader('.json-to-csv-btn, [class*="csv"] button, button'),
			});
		}
		if (envHost === 'stage') {
			items.push({
				label: 'Escrow account',
				onClick: () => clickInHeader('[class*="fundEscrow"] button'),
			});
		}
		return items;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundDetailList.length, clickInHeader]);

	const handleClickBtn = useCallback(() => {
		navigate(`${FUNDS}`);
		setSelectedIdState([]);
		// eslint-disable-next-line
	}, []);

	/* ------------------------------------------------------------------ sv2 */

	const investors = useMemo(() => toInvestorRows(fundDetailList), [fundDetailList]);

	const shown = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return investors;
		// same client-side filter the old table did, spelled out
		return investors.filter(r =>
			[r.name, r.email, r.phone, r.shareClass, r.round]
				.join(' ')
				.toLowerCase()
				.includes(q)
		);
	}, [investors, search]);

	const money = useCallback(
		(v: number) => `$${numberDecimal(Number(v || 0))}`,
		[numberDecimal]
	);

	return (
		<>
			<div className="sv2-scope sv2 funds-detail-deck">
				<PageHead
					title={fundName || 'Fund'}
					sub={
						<>
							<span className="reinv" onClick={handleClickBtn}>
								&lsaquo; Funds
							</span>{' '}
							· ID : {fundId}
							{fundRound ? ` · ${fundRound} round` : ''}
						</>
					}
					action={
						<>
							{isUserPermissionWrite && (
								<button
									className="cta"
									type="button"
									onClick={openAddInvestor}
								>
									&#65291; Add Investor
								</button>
							)}
							<span className="kwrap">
								<Kebab items={kebabItems} />
							</span>
						</>
					}
				/>

				{/* Derived from FundDetailState — the payload this table renders — so
					the strip cannot disagree with the rows beneath it (§5.1.1). */}
				<div className="strip fstrip">
					<div className="tile">
						<div className="l">Investors</div>
						<div className="n">{investors.length}</div>
					</div>
					<div className="tile">
						<div className="l">Pending investors</div>
						<div className="n">{pendingInvestors(investors)}</div>
					</div>
					<div className="tile">
						<div className="l">Total raised (USD)</div>
						<div className="n gr">{money(totalRaised(investors))}</div>
					</div>
					<div className="tile">
						<div className="l">Raised pending (USD)</div>
						<div className="n am">{money(pendingRaised(investors))}</div>
					</div>
					<div className="tile">
						<div className="l">Post-money valuation (USD)</div>
						{/* `--` rather than a false $0.00 when the fund record is not in
							FundsState — the cold-mount case (§5.1.2) */}
						<div className="n">
							{fundPostMoney ? money(fundPostMoney) : '--'}
						</div>
					</div>
				</div>

				<div className="ctl">
					<SearchBox
						value={search}
						onChange={setSearch}
						placeholder="Search investors"
						title="Searches name, email, phone, round and share class"
					/>
				</div>

				<table className="stbl">
					<thead>
						<tr>
							<th className="invcol">Investor</th>
							<th>Contact</th>
							<th className="num">Invested</th>
							<th className="num">Shares</th>
							<th className="num">Price / share</th>
							<th>Issued</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{FundDetailLoading && (
							<tr>
								<td colSpan={7} className="sv2-loading">
									Loading investors…
								</td>
							</tr>
						)}
						{!FundDetailLoading && !investors.length && (
							<tr>
								<td colSpan={7} className="sv2-empty">
									<div>No investors yet</div>
									<div className="sub">
										Add investors by pasting a cap table.
									</div>
									{isUserPermissionWrite && (
										<button
											className="cta"
											type="button"
											onClick={openAddInvestor}
										>
											&#65291; Add Investor
										</button>
									)}
								</td>
							</tr>
						)}
						{!FundDetailLoading && !!investors.length && !shown.length && (
							<tr>
								<td colSpan={7} className="sv2-empty">
									No investor matches this search.
								</td>
							</tr>
						)}
						{shown.map(r => {
							const prov = provenance(r);
							const steps = toSteps(r);
							return (
								<tr key={r.id}>
									<td className="od invcol">
										<span className="nm">{r.name || '--'}</span>{' '}
										<span className={`vch ${prov.verdict}`}>{prov.label}</span>
										{!!steps.length && (
											<>
												<div className="chain">
													<StepChain steps={steps} />
												</div>
												<div className="fnm">
													<span
														className="vcf"
														onClick={e => handleViewStepDesign(e, r.session)}
													>
														View complete flow
													</span>
												</div>
											</>
										)}
									</td>
									<td>
										<span className="mail" title={r.email}>
											{r.email || '--'}
										</span>
										{r.phone && <div className="fund">{r.phone}</div>}
									</td>
									<td className="num">
										<span className="nm">{money(r.invested)}</span>
										{r.round && <div className="fund">{r.round}</div>}
									</td>
									<td className="num">
										<span className="nm">{r.shares.toLocaleString()}</span>
										{r.shareClass && (
											<div className="fund">Class {r.shareClass}</div>
										)}
									</td>
									<td className="num">
										<span className="nm">{money(r.price)}</span>
									</td>
									<td>
										<span className="meta">
											{r.issued && getDate(r.issued) !== 'Invalid DateTime'
												? getDate(r.issued)
												: '--'}
										</span>
									</td>
									<td>
										{/* absent, not disabled — a pasted investor has no session
											to view, and a greyed control that can never act reads
											as a label (D25/D26) */}
										{!!r.sessionId && (
											<span
												className="reinv"
												onClick={handleClickViewIcon(r.raw)}
											>
												View session
											</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>

				{/* keeps every modal, store and the cap-table localStorage hand-off;
					renders none of its own chrome */}
				<FundIconHeader
					fundId={fundId}
					isUserPermissionWrite={isUserPermissionWrite}
					chromeless
					api={headerApi}
				/>

				<div className="foot">
					A <b>VERIFIED</b> investor completed a payment step in their own
					session. <b>ADDED MANUALLY</b> means the row was pasted from a cap
					table and no one has checked it.
				</div>
			</div>
			<Modal
				isOpen={isModalOpen}
				closeModal={handleCloseViewModal}
				className="react-modal_kycDetail"
				modalName="Session modal"
				optionalClassName="override__padding"
			>
				<ViewKyc
					sessionKybLoaded={sessionKybLoaded}
					sessionKycLoaded={sessionKycLoaded}
					sessionAmlLoaded={sessionAmlLoaded}
					sessionSignLoaded={sessionSignLoaded}
					sessionFundLoaded={sessionFundLoaded}
					sessionAccreditationLoaded={sessionAccreditationLoaded}
					activityLoaded={activityLoaded}
					sessionDeviceLoaded={sessionDeviceLoaded}
					isFundView
				/>
			</Modal>
			{isComplexOpen && (
				<Modal
					isOpen={isComplexOpen}
					closeModal={() => setIsComplexOpen(false)}
					className="react-modal_kycDetail"
					modalName="Session modal"
				>
					<ComplexSessionView
						isLoading={complexSessionDetailsLoading || isNodesSession}
					/>
				</Modal>
			)}
			{openConditionalView && (
				<ComplexStepsView
					isOpen={openConditionalView}
					closeModal={handleCloseModal}
					details={selectedItem}
				/>
			)}
		</>
	);
};

export default FundDetails;
